/**
 * @file UnsubscribePage.jsx
 * @description Public, logged-out page behind the one-click unsubscribe link
 * in every cold-outreach email: `abacases.com/unsubscribe?token=<uuid>`.
 *
 * Mirrors InterviewRespondPage.jsx's reasoning exactly (see that file's
 * header for the full writeup): the edge-function gateway cannot serve HTML
 * (rewrites Content-Type to text/plain), and a `<ref>.supabase.co` link in an
 * inbox reads as phishing where `abacases.com` reads as the product.
 *
 * GET previews the token (masked email, zero writes) on mount; the one
 * button on this page is what actually unsubscribes, via POST — a mail
 * security scanner prefetching the GET link must never trigger the write.
 *
 * Logged out by design: mounted from App.js ahead of every gate, same as
 * interview-respond, since mail gets forwarded and `info@` addresses are
 * shared inboxes. Cold-outreach is English-only today (see the email
 * templates in scripts/send-*-outreach.py), so this page is too — no new
 * i18n keys.
 *
 * @module components/UnsubscribePage
 */
import { useCallback, useEffect, useState } from "react";
import { T } from "../theme";
import { Btn } from "./UI";
import Logo from "./Logo";
import { CheckCircleIcon, AlertTriangleIcon } from "./Icons";
import { usePageSeo } from "../lib/seo";
import { readUnsubscribeToken, previewUnsubscribe, confirmUnsubscribe } from "../lib/unsubscribe";

export default function UnsubscribePage() {
  usePageSeo({ title: "Unsubscribe | ABAcases", noindex: true });

  const [token] = useState(() => (typeof window === "undefined" ? "" : readUnsubscribeToken(window.location.search)));
  const [phase, setPhase] = useState(token ? "loading" : "invalid"); // loading | ready | submitting | done | invalid | error
  const [maskedEmail, setMaskedEmail] = useState("");

  const load = useCallback(() => {
    if (!token) { setPhase("invalid"); return; }
    setPhase("loading");
    previewUnsubscribe(token).then((res) => {
      if (res.ok) {
        setMaskedEmail(res.maskedEmail || "");
        setPhase("ready");
      } else if (res.reason === "invalid_token") {
        setPhase("invalid");
      } else {
        setPhase("error");
      }
    });
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const submit = useCallback(() => {
    setPhase("submitting");
    confirmUnsubscribe(token).then((res) => {
      if (res.ok) {
        setMaskedEmail(res.maskedEmail || maskedEmail);
        setPhase("done");
      } else {
        setPhase("error");
      }
    });
  }, [token, maskedEmail]);

  let s;
  if (phase === "loading") {
    s = { tone: "info", title: "Looking up your link…" };
  } else if (phase === "invalid") {
    s = {
      tone: "warn", icon: AlertTriangleIcon,
      title: "This link no longer works.",
      body: "It's already been used, or it's expired. If you're still getting emails from us, just reply to one directly and we'll remove you by hand.",
    };
  } else if (phase === "error") {
    s = {
      tone: "warn", icon: AlertTriangleIcon,
      title: "Something went wrong.",
      body: "Reply to the email directly and we'll remove you by hand right away, or try this link again.",
      retry: true,
    };
  } else if (phase === "done") {
    s = {
      tone: "ok", icon: CheckCircleIcon,
      title: "You're unsubscribed.",
      body: maskedEmail
        ? `You won't get any more emails from ABAcases at ${maskedEmail}.`
        : "You won't get any more emails from ABAcases at this address.",
    };
  } else {
    // ready
    s = {
      tone: "info", icon: AlertTriangleIcon,
      title: "Unsubscribe from ABAcases emails?",
      body: maskedEmail ? `This removes ${maskedEmail} from future outreach emails.` : "This removes your address from future outreach emails.",
      button: "Unsubscribe",
    };
  }

  const accent = s.tone === "warn" ? T.red : s.tone === "ok" ? T.green : T.blue;
  const Icon = s.icon;
  const busy = phase === "loading" || phase === "submitting";

  return (
    <div style={{
      minHeight: "100vh", background: T.bg, fontFamily: T.fontBody, color: T.text,
      padding: "32px 16px calc(48px + env(safe-area-inset-bottom, 0px))",
      display: "flex", flexDirection: "column", alignItems: "center",
    }}>
      <div style={{ marginBottom: 20 }}>
        <Logo variant="lockup" height={30} alt="ABAcases" />
      </div>

      <div style={{
        width: "100%", maxWidth: 460, background: T.surface,
        border: `1px solid ${T.border}`, borderRadius: T.radiusLg,
        boxShadow: T.shadow, padding: "26px 22px 24px", boxSizing: "border-box",
      }}>
        {Icon && (
          <div style={{
            width: 40, height: 40, borderRadius: T.radiusFull, background: `${accent}14`,
            display: "flex", alignItems: "center", justifyContent: "center", marginBottom: 14,
          }}>
            <Icon size={20} color={accent} />
          </div>
        )}

        <h1 style={{
          margin: 0, fontSize: 20, lineHeight: 1.3, fontWeight: 800,
          letterSpacing: "-0.02em", color: T.text,
        }}>
          {s.title}
        </h1>

        {s.body && (
          <p style={{ margin: "12px 0 0", fontSize: 14.5, lineHeight: 1.6, color: T.textMid }}>
            {s.body}
          </p>
        )}

        {s.button && (
          <div style={{ marginTop: 20 }}>
            <Btn
              variant="outline"
              onClick={submit}
              disabled={busy}
              style={{ width: "100%", padding: "13px 20px", fontSize: 15.5, borderRadius: T.radiusMd }}
            >
              {phase === "submitting" ? "Unsubscribing…" : s.button}
            </Btn>
          </div>
        )}

        {s.retry && (
          <div style={{ marginTop: 20 }}>
            <Btn
              variant="outline"
              onClick={load}
              disabled={busy}
              style={{ width: "100%", padding: "13px 20px", fontSize: 15.5, borderRadius: T.radiusMd }}
            >
              Try again
            </Btn>
          </div>
        )}
      </div>
    </div>
  );
}
